/**
 * 势力列表组件 - 天下大势页面
 * 显示本国和已知势力，按邻近度排序
 */

import { useState } from 'react'
import type { Faction } from '../data/shangDynastyData'
import { getCitiesByFaction } from '../data/shangDynastyData'
import './FactionList.css'

interface FactionListProps {
  playerFactionId: string
  factions: Faction[]
  onFactionSelect?: (faction: Faction) => void
}

/** 势力信息获取渠道 */
type InformationChannel = 'default' | 'spy' | 'alliance' | 'marriage'

/** 势力信息可见性 */
interface FactionVisibility {
  [factionId: string]: {
    channel: InformationChannel
    level: number // 1-3，信息完整度
  }
}

/** 势力属性 */
interface FactionAttributes {
  legitimacy: number
  prestige: number
  culture: number
  military: number
  economy: number
  popularity: number
}

/** 势力君主和都城信息 */
interface FactionDetails {
  [factionId: string]: {
    ruler: string
    capital: {
      name: string
      location: string
    }
    attributes: FactionAttributes
    school: string
  }
}

/** 势力邻近度计算 */
const calculateProximity = (factionId: string, playerFactionId: string): number => {
  // 模拟邻近度计算
  const proximityMap: Record<string, Record<string, number>> = {
    shang: { shang: 0, zhoufang: 1, jingfang: 2, yuguo: 1, chongguo: 2, tufang: 3, qiangfang: 3, guifang: 4, renfang: 3, yufang: 2, zhaofang: 2, sanxingdui: 5 },
    zhoufang: { zhoufang: 0, shang: 1, chongguo: 1, qiangfang: 2, zhaofang: 1, yuguo: 2, jingfang: 3, tufang: 3, guifang: 4, renfang: 5, yufang: 3, sanxingdui: 4 },
    guifang: { guifang: 0, tufang: 1, jingfang: 2, shang: 4, qiangfang: 3, zhoufang: 4, renfang: 5, yufang: 6, zhaofang: 5, chongguo: 4, yuguo: 3, sanxingdui: 6 },
    qiangfang: { qiangfang: 0, zhoufang: 2, chongguo: 2, zhaofang: 3, shang: 3, tufang: 3, guifang: 3, renfang: 6, yufang: 4, jingfang: 4, yuguo: 3, sanxingdui: 3 },
    tufang: { tufang: 0, guifang: 1, jingfang: 1, yuguo: 2, shang: 3, zhoufang: 3, qiangfang: 3, renfang: 4, yufang: 5, zhaofang: 4, chongguo: 3, sanxingdui: 6 },
    renfang: { renfang: 0, shang: 3, jingfang: 3, yuguo: 4, zhoufang: 5, tufang: 4, guifang: 5, qiangfang: 6, yufang: 4, zhaofang: 6, chongguo: 5, sanxingdui: 7 },
    yufang: { yufang: 0, shang: 2, zhaofang: 3, zhoufang: 3, qiangfang: 4, renfang: 4, jingfang: 3, yuguo: 2, tufang: 5, guifang: 6, chongguo: 3, sanxingdui: 5 },
    zhaofang: { zhaofang: 0, zhoufang: 1, chongguo: 1, qiangfang: 3, yufang: 3, shang: 2, yuguo: 2, jingfang: 3, tufang: 4, guifang: 5, renfang: 6, sanxingdui: 4 },
    jingfang: { jingfang: 0, tufang: 1, shang: 2, yuguo: 2, guifang: 2, renfang: 3, zhoufang: 3, qiangfang: 4, yufang: 3, zhaofang: 3, chongguo: 3, sanxingdui: 6 },
    chongguo: { chongguo: 0, zhoufang: 1, qiangfang: 2, zhaofang: 1, shang: 2, yufang: 3, yuguo: 2, jingfang: 3, tufang: 3, guifang: 4, renfang: 5, sanxingdui: 4 },
    yuguo: { yuguo: 0, shang: 1, jingfang: 2, tufang: 2, zhoufang: 2, chongguo: 2, zhaofang: 2, qiangfang: 3, yufang: 2, guifang: 3, renfang: 4, sanxingdui: 5 },
    sanxingdui: { sanxingdui: 0, qiangfang: 3, zhoufang: 4, zhaofang: 4, yufang: 5, chongguo: 4, shang: 5, yuguo: 5, jingfang: 6, tufang: 6, guifang: 6, renfang: 7 }
  }
  
  return proximityMap[playerFactionId]?.[factionId] || 10
}

/** 势力详细信息 */
const factionDetails: FactionDetails = {
  shang: {
    ruler: '商纣王',
    capital: {
      name: '殷',
      location: '河南安阳'
    },
    attributes: {
      legitimacy: 8,
      prestige: 9,
      culture: 10,
      military: 7,
      economy: 8,
      popularity: 5
    },
    school: '青铜文明'
  },
  zhoufang: {
    ruler: '周文王',
    capital: {
      name: '岐山',
      location: '陕西岐山'
    },
    attributes: {
      legitimacy: 6,
      prestige: 7,
      culture: 8,
      military: 8,
      economy: 7,
      popularity: 9
    },
    school: '礼乐文化'
  },
  guifang: {
    ruler: '鬼方单于',
    capital: {
      name: '鬼方城',
      location: '蒙古草原'
    },
    attributes: {
      legitimacy: 4,
      prestige: 5,
      culture: 3,
      military: 9,
      economy: 4,
      popularity: 6
    },
    school: '游牧文明'
  },
  qiangfang: {
    ruler: '羌方首领',
    capital: {
      name: '羌方城',
      location: '甘肃一带'
    },
    attributes: {
      legitimacy: 3,
      prestige: 4,
      culture: 4,
      military: 8,
      economy: 3,
      popularity: 7
    },
    school: '山地文明'
  },
  tufang: {
    ruler: '土方首领',
    capital: {
      name: '土方城',
      location: '山西北部'
    },
    attributes: {
      legitimacy: 4,
      prestige: 5,
      culture: 3,
      military: 7,
      economy: 4,
      popularity: 6
    },
    school: '草原文明'
  },
  renfang: {
    ruler: '人方首领',
    capital: {
      name: '夷师',
      location: '山东半岛'
    },
    attributes: {
      legitimacy: 5,
      prestige: 6,
      culture: 7,
      military: 6,
      economy: 8,
      popularity: 7
    },
    school: '东夷文明'
  },
  yufang: {
    ruler: '盂方首领',
    capital: {
      name: '盂方城',
      location: '河南南部'
    },
    attributes: {
      legitimacy: 4,
      prestige: 5,
      culture: 6,
      military: 5,
      economy: 7,
      popularity: 6
    },
    school: '中原文明'
  },
  zhaofang: {
    ruler: '召方首领',
    capital: {
      name: '召方城',
      location: '陕西南部'
    },
    attributes: {
      legitimacy: 5,
      prestige: 4,
      culture: 5,
      military: 4,
      economy: 5,
      popularity: 7
    },
    school: '山地文明'
  },
  jingfang: {
    ruler: '井方首领',
    capital: {
      name: '井方城',
      location: '河北南部'
    },
    attributes: {
      legitimacy: 6,
      prestige: 5,
      culture: 6,
      military: 5,
      economy: 6,
      popularity: 8
    },
    school: '中原文明'
  },
  chongguo: {
    ruler: '崇侯虎',
    capital: {
      name: '崇城',
      location: '陕西西安'
    },
    attributes: {
      legitimacy: 5,
      prestige: 4,
      culture: 5,
      military: 6,
      economy: 5,
      popularity: 5
    },
    school: '关中文明'
  },
  yuguo: {
    ruler: '邘国首领',
    capital: {
      name: '邘城',
      location: '河南沁阳'
    },
    attributes: {
      legitimacy: 4,
      prestige: 3,
      culture: 4,
      military: 3,
      economy: 4,
      popularity: 6
    },
    school: '中原文明'
  },
  sanxingdui: {
    ruler: '古蜀王',
    capital: {
      name: '三星堆',
      location: '四川广汉'
    },
    attributes: {
      legitimacy: 7,
      prestige: 6,
      culture: 10,
      military: 5,
      economy: 8,
      popularity: 8
    },
    school: '古蜀文明'
  }
}

/** 势力列表组件 */
export function FactionList({ playerFactionId, factions, onFactionSelect }: FactionListProps) {
  const [selectedFaction, setSelectedFaction] = useState<Faction | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  
  // 模拟信息可见性数据
  const [visibility, _setVisibility] = useState<FactionVisibility>({
    shang: { channel: 'default', level: 3 }, // 本国，信息完整
    zhoufang: { channel: 'default', level: 2 }, // 邻近势力，基础信息
    jingfang: { channel: 'default', level: 2 },
    yuguo: { channel: 'default', level: 1 },
    chongguo: { channel: 'spy', level: 2 }, // 有间谍，信息较多
    zhaofang: { channel: 'default', level: 1 },
    yufang: { channel: 'alliance', level: 3 }, // 盟友，信息完整
    qiangfang: { channel: 'default', level: 1 },
    guifang: { channel: 'marriage', level: 2 }, // 联姻，信息较多
    tufang: { channel: 'default', level: 1 },
    renfang: { channel: 'default', level: 1 },
    sanxingdui: { channel: 'default', level: 1 }
  })

  // 过滤出已知势力并按邻近度排序
  const knownFactions = factions.filter(faction => 
    visibility[faction.id] !== undefined
  )

  // 排序：本国 > 邻近势力 > 远国
  const sortedFactions = [...knownFactions].sort((a, b) => {
    if (a.id === playerFactionId) return -1
    if (b.id === playerFactionId) return 1
    return calculateProximity(a.id, playerFactionId) - calculateProximity(b.id, playerFactionId)
  })

  // 获取玩家势力
  const playerFaction = factions.find(faction => faction.id === playerFactionId) || sortedFactions[0]

  // 处理势力选择
  const handleFactionSelect = (faction: Faction) => {
    setSelectedFaction(faction)
    setShowDetails(true)
    onFactionSelect?.(faction)
  }

  // 关闭详情
  const handleCloseDetails = () => {
    setShowDetails(false)
    setSelectedFaction(null)
  }

  // 获取信息可见性文本
  const getVisibilityText = (factionId: string) => {
    const info = visibility[factionId]
    if (!info) return '未知'
    
    switch (info.channel) {
      case 'default':
        return '基础情报'
      case 'spy':
        return '间谍侦察'
      case 'alliance':
        return '同盟共享'
      case 'marriage':
        return '联姻往来'
      default:
        return '基础情报'
    }
  }

  // 获取势力详情
  const getFactionDetail = (factionId: string) => {
    return factionDetails[factionId] || {
      ruler: '未知',
      capital: {
        name: '未知',
        location: '未知'
      },
      attributes: {
        legitimacy: 0,
        prestige: 0,
        culture: 0,
        military: 0,
        economy: 0,
        popularity: 0
      },
      school: '未知'
    }
  }

  return (
    <div className="faction-list-page">
      {/* 势力列表 */}
      <div className="faction-list">
        {/* 本国势力 */}
        {playerFaction && (
          <div className="faction-section">
            <h2 className="section-title">本国势力</h2>
            <div className="faction-grid">
              <div 
                className="faction-card player-faction"
                onClick={() => handleFactionSelect(playerFaction)}
              >
                <div 
                  className="faction-color-bar" 
                  style={{ backgroundColor: playerFaction.color }}
                />
                <div className="faction-content">
                  <div className="faction-header">
                    <h3 className="faction-name">{playerFaction.name}</h3>
                    <span className="faction-status">本国</span>
                  </div>
                  <div className="basic-info-grid">
                    <p className="faction-ruler">君主：{getFactionDetail(playerFaction.id).ruler}</p>
                    <p className="faction-capital">都城：{getFactionDetail(playerFaction.id).capital.name}</p>
                    <p className="faction-school">流派：{getFactionDetail(playerFaction.id).school}</p>
                  </div>
                  <div className="faction-attributes">
                    <div className="attribute-item">
                      <span className="attribute-label">正统性</span>
                      <span className="attribute-value">{getFactionDetail(playerFaction.id).attributes.legitimacy}</span>
                    </div>
                    <div className="attribute-item">
                      <span className="attribute-label">威望</span>
                      <span className="attribute-value">{getFactionDetail(playerFaction.id).attributes.prestige}</span>
                    </div>
                  </div>
                  <button className="details-btn"><span>查看详情</span></button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 其他势力 */}
        <div className="faction-section">
          <h2 className="section-title">已知势力</h2>
          <div className="faction-grid">
            {sortedFactions.filter(f => f.id !== playerFactionId).map((faction) => {
              const prox = calculateProximity(faction.id, playerFactionId)
              const proximityText = prox <= 1 ? '邻近' : prox <= 3 ? '较近' : '遥远'
              const detail = getFactionDetail(faction.id)
              
              return (
                <div 
                  key={faction.id}
                  className="faction-card"
                  onClick={() => handleFactionSelect(faction)}
                >
                  <div 
                    className="faction-color-bar" 
                    style={{ backgroundColor: faction.color }}
                  />
                  <div className="faction-content">
                    <div className="faction-header">
                      <h3 className="faction-name">{faction.name}</h3>
                      <span className={`faction-distance ${proximityText}`}>{proximityText}</span>
                    </div>
                    <div className="basic-info-grid">
                      <p className="faction-ruler">君主：{detail.ruler}</p>
                      <p className="faction-capital">都城：{detail.capital.name}</p>
                      <p className="faction-school">流派：{detail.school}</p>
                    </div>
                    <div className="faction-attributes">
                      <div className="attribute-item">
                        <span className="attribute-label">正统性</span>
                        <span className="attribute-value">
                          {visibility[faction.id]?.level >= 2 ? detail.attributes.legitimacy : '未知'}
                        </span>
                      </div>
                      <div className="attribute-item">
                        <span className="attribute-label">威望</span>
                        <span className="attribute-value">
                          {visibility[faction.id]?.level >= 2 ? detail.attributes.prestige : '未知'}
                        </span>
                      </div>
                    </div>
                    <div className="faction-info-source">
                      <span className="info-source">{getVisibilityText(faction.id)}</span>
                    </div>
                    <button className="details-btn"><span>查看详情</span></button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* 势力详情弹窗 */}
      {showDetails && selectedFaction && (
        <div className="faction-details-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{selectedFaction.name} - 势力详情</h2>
              <button className="close-btn" onClick={handleCloseDetails}>×</button>
            </div>
            
            <div className="modal-body">
              {/* 基本信息 */}
              <div className="detail-section">
                <h3>基本信息</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">君主</span>
                    <span className="detail-value">{getFactionDetail(selectedFaction.id).ruler}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">都城</span>
                    <span className="detail-value">{getFactionDetail(selectedFaction.id).capital.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">位置</span>
                    <span className="detail-value">{getFactionDetail(selectedFaction.id).capital.location}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">流派</span>
                    <span className="detail-value">{getFactionDetail(selectedFaction.id).school}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">情报来源</span>
                    <span className="detail-value">{getVisibilityText(selectedFaction.id)}</span>
                  </div>
                </div>
              </div>

              {/* 属性信息 */}
              <div className="detail-section">
                <h3>势力属性</h3>
                <div className="attributes-grid">
                  <div className="attribute-detail">
                    <span className="attribute-name">正统性</span>
                    <div className="attribute-bar">
                      <div 
                        className="attribute-fill" 
                        style={{ 
                          width: `${(visibility[selectedFaction.id]?.level >= 1 ? getFactionDetail(selectedFaction.id).attributes.legitimacy : 0) * 10}%`,
                          backgroundColor: selectedFaction.color
                        }}
                      />
                    </div>
                    <span className="attribute-number">
                      {visibility[selectedFaction.id]?.level >= 1 ? getFactionDetail(selectedFaction.id).attributes.legitimacy : '未知'}
                    </span>
                  </div>
                  <div className="attribute-detail">
                    <span className="attribute-name">威望</span>
                    <div className="attribute-bar">
                      <div 
                        className="attribute-fill" 
                        style={{ 
                          width: `${(visibility[selectedFaction.id]?.level >= 1 ? getFactionDetail(selectedFaction.id).attributes.prestige : 0) * 10}%`,
                          backgroundColor: selectedFaction.color
                        }}
                      />
                    </div>
                    <span className="attribute-number">
                      {visibility[selectedFaction.id]?.level >= 1 ? getFactionDetail(selectedFaction.id).attributes.prestige : '未知'}
                    </span>
                  </div>
                  <div className="attribute-detail">
                    <span className="attribute-name">文化</span>
                    <div className="attribute-bar">
                      <div 
                        className="attribute-fill" 
                        style={{ 
                          width: `${(visibility[selectedFaction.id]?.level >= 2 ? getFactionDetail(selectedFaction.id).attributes.culture : 0) * 10}%`,
                          backgroundColor: selectedFaction.color
                        }}
                      />
                    </div>
                    <span className="attribute-number">
                      {visibility[selectedFaction.id]?.level >= 2 ? getFactionDetail(selectedFaction.id).attributes.culture : '未知'}
                    </span>
                  </div>
                  <div className="attribute-detail">
                    <span className="attribute-name">军事</span>
                    <div className="attribute-bar">
                      <div 
                        className="attribute-fill" 
                        style={{ 
                          width: `${(visibility[selectedFaction.id]?.level >= 2 ? getFactionDetail(selectedFaction.id).attributes.military : 0) * 10}%`,
                          backgroundColor: selectedFaction.color
                        }}
                      />
                    </div>
                    <span className="attribute-number">
                      {visibility[selectedFaction.id]?.level >= 2 ? getFactionDetail(selectedFaction.id).attributes.military : '未知'}
                    </span>
                  </div>
                  <div className="attribute-detail">
                    <span className="attribute-name">经济</span>
                    <div className="attribute-bar">
                      <div 
                        className="attribute-fill" 
                        style={{ 
                          width: `${(visibility[selectedFaction.id]?.level >= 2 ? getFactionDetail(selectedFaction.id).attributes.economy : 0) * 10}%`,
                          backgroundColor: selectedFaction.color
                        }}
                      />
                    </div>
                    <span className="attribute-number">
                      {visibility[selectedFaction.id]?.level >= 2 ? getFactionDetail(selectedFaction.id).attributes.economy : '未知'}
                    </span>
                  </div>
                  <div className="attribute-detail">
                    <span className="attribute-name">民心</span>
                    <div className="attribute-bar">
                      <div 
                        className="attribute-fill" 
                        style={{ 
                          width: `${(visibility[selectedFaction.id]?.level >= 3 ? getFactionDetail(selectedFaction.id).attributes.popularity : 0) * 10}%`,
                          backgroundColor: selectedFaction.color
                        }}
                      />
                    </div>
                    <span className="attribute-number">
                      {visibility[selectedFaction.id]?.level >= 3 ? getFactionDetail(selectedFaction.id).attributes.popularity : '未知'}
                    </span>
                  </div>
                </div>
              </div>

              {/* 城市信息 */}
              <div className="detail-section">
                <h3>主要城市</h3>
                <div className="cities-list">
                  {visibility[selectedFaction.id]?.level >= 2 ? (
                    getCitiesByFaction(selectedFaction.id).slice(0, 3).map((city, index) => (
                      <div key={index} className="city-item">
                        <span className="city-name">{city.name}</span>
                        <span className="city-type">{city.level === 'capital' ? '都城' : city.level === 'large' ? '大城' : city.level === 'medium' ? '中城' : '小城'}</span>
                      </div>
                    ))
                  ) : (
                    <p className="unknown-info">情报不足</p>
                  )}
                </div>
              </div>

              {/* 外交关系 */}
              <div className="detail-section">
                <h3>外交关系</h3>
                {visibility[selectedFaction.id]?.level >= 1 ? (
                  <div className="diplomacy-info">
                    <p>与我国关系：{selectedFaction.id === playerFactionId ? '本国' : '中立'}</p>
                    {visibility[selectedFaction.id]?.level >= 2 && (
                      <p>特性：{selectedFaction.traits?.slice(0, 2).join('、') || '暂无'}</p>
                    )}
                  </div>
                ) : (
                  <p className="unknown-info">情报不足</p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="close-modal-btn" onClick={handleCloseDetails}>
                关闭
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
